import { useCallback, useEffect, useRef, useState } from 'react';

import useOCR from '../../../hooks/use-ocr';
import { OCRMatch } from '../../../types/ocr';
import { captureVideoFrame, measureBrightness } from '../../../utils/image-utils';
import BarcodeDisplay from '../../Shared/BarcodeDisplay/BarcodeDisplay';
import { barcodeShortLabel } from '../../../utils/barcode-format';

import './OCRCapture.scss';

interface OCRCaptureProps {
  boxes: Array<{ id?: number; barcode: string | null; scanned?: boolean }>;
  onMatch: (identifier: string, match: OCRMatch) => void;
  onClose: () => void;
  onError?: (error: string) => void;
}

function OCRCapture({ boxes, onMatch, onClose, onError }: OCRCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<OCRMatch | null>(null);
  const [permissionState, setPermissionState] = useState<'checking' | 'prompt' | 'denied' | 'ready'>('checking');
  const [brightnessWarning, setBrightnessWarning] = useState(false);

  const {
    captureAndExtract,
    uiState,
    resetState,
  } = useOCR();

  // Get expected identifiers from boxes that haven't been scanned yet
  // Use barcode if available, otherwise fall back to box ID
  const expectedBarcodes = boxes
    .filter((box) => !box.scanned)
    .map((box) => box.barcode || String(box.id || ''))
    .filter((id) => id.length > 0);

  // Check permission state then initialize camera
  useEffect(() => {
    let stream: MediaStream | null = null;

    const checkAndInit = async () => {
      // Check if we can query camera permission
      let camState: string = 'unknown';
      try {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
          camState = result.state;
        }
      } catch {
        // iOS Safari doesn't support camera permission query — proceed directly
      }

      if (camState === 'denied') {
        setPermissionState('denied');
        onError?.('Camera access denied — enable it in your browser settings');
        return;
      }

      // If the browser will show a prompt, briefly explain why
      if (camState === 'prompt') {
        setPermissionState('prompt');
        // Small delay so the user sees the explanation before the browser prompt
        await new Promise(r => setTimeout(r, 800));
      }

      setPermissionState('ready');

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setHasCamera(true);
        }
      } catch (error) {
        console.error('[OCRCapture] Camera access failed:', error);
        setPermissionState('denied');
        onError?.('Camera access denied or unavailable');
        setHasCamera(false);
      }
    };

    checkAndInit();

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [onError]);

  // Handle capture button click
  const handleCapture = useCallback(async () => {
    if (!videoRef.current || !hasCamera) return;

    // Quick brightness check
    const frame = captureVideoFrame(videoRef.current);
    if (frame) {
      const brightness = measureBrightness(frame);
      setBrightnessWarning(brightness < 60);
    }

    setSelectedMatch(null);
    const result = await captureAndExtract(
      videoRef.current,
      expectedBarcodes
    );

    if (result.success && result.matches.length > 0) {
      // If we have a high-confidence exact match, use it immediately
      const bestMatch = result.matches[0];
      if (bestMatch.isExactMatch && bestMatch.confidence > 0.8) {
        handleConfirmMatch(bestMatch);
      } else if (result.matches.length === 1) {
        // Single match - auto-select but let user confirm
        setSelectedMatch(bestMatch);
      }
      // Otherwise, user will need to select from matches
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureAndExtract, expectedBarcodes, hasCamera]);

  // Handle user selecting a match
  const handleSelectMatch = useCallback((match: OCRMatch) => {
    setSelectedMatch(match);
  }, []);

  // Handle user confirming a match
  const handleConfirmMatch = useCallback(
    (match: OCRMatch) => {
      onMatch(match.barcode, match);
      resetState();
      setSelectedMatch(null);
    },
    [onMatch, resetState]
  );

  // Handle retry
  const handleRetry = useCallback(() => {
    resetState();
    setSelectedMatch(null);
  }, [resetState]);

  // Get UI content based on state
  const renderContent = () => {
    const { processingState, lastResult } = uiState;

    // Capturing state
    if (processingState === 'capturing') {
      return (
        <div className="ocr-status">
          <div className="spinner" />
          <p>Capturing image...</p>
        </div>
      );
    }

    // Processing state
    if (processingState === 'processing') {
      return (
        <div className="ocr-status">
          <div className="spinner" />
          <p>Analyzing text...</p>
        </div>
      );
    }

    // Queued state (offline)
    if (processingState === 'queued') {
      return (
        <div className="ocr-status queued">
          <div className="icon">&#x1F4E4;</div>
          <p>Request queued</p>
          <p className="subtitle">Will process when online</p>
          <button className="secondary" onClick={handleRetry}>
            Try Again
          </button>
        </div>
      );
    }

    // Success state with matches
    if (processingState === 'success' && lastResult?.matches.length) {
      return (
        <div className="ocr-results">
          <h4>Matches Found</h4>
          {selectedMatch && (
            <button
              className="confirm-btn"
              onClick={() => handleConfirmMatch(selectedMatch)}
            >
              Confirm: {barcodeShortLabel(selectedMatch.barcode)}
            </button>
          )}
          <div className="matches-list">
            {lastResult.matches.map((match, index) => (
              <div
                key={`${match.barcode}-${index}`}
                className={`match-item ${
                  selectedMatch?.barcode === match.barcode ? 'selected' : ''
                } ${match.isExactMatch ? 'exact' : ''}`}
                onClick={() => handleSelectMatch(match)}
              >
                <div className="match-barcode">
                  <BarcodeDisplay barcode={match.barcode} />
                </div>
                <div className="match-info">
                  <span className={`confidence ${getConfidenceClass(match)}`}>
                    {getConfidenceLabel(match)}
                  </span>
                  {!match.isExactMatch && (
                    <span className="edit-distance">
                      {match.editDistance} char diff
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button className="secondary" onClick={handleRetry}>
            Scan Again
          </button>
        </div>
      );
    }

    // Error or no match state
    if (processingState === 'error' || processingState === 'success') {
      const errorMessage =
        lastResult?.error ||
        (lastResult?.fullText
          ? 'No matching box number found in image'
          : 'Could not read text from image');

      return (
        <div className="ocr-status error">
          <div className="icon">&#x26A0;</div>
          <p>{errorMessage}</p>
          {lastResult?.fullText && (
            <div className="extracted-text">
              <small>Extracted: "{lastResult.fullText.slice(0, 50)}..."</small>
            </div>
          )}
          <button className="secondary" onClick={handleRetry}>
            Try Again
          </button>
        </div>
      );
    }

    // Idle state - ready to capture
    return (
      <div className="ocr-instructions">
        <p>Position the printed box number in the frame</p>
        <p className="hint">Hold steady, fill the frame with the label</p>
        {brightnessWarning && (
          <p className="brightness-warn">Too dark — move to better lighting</p>
        )}
      </div>
    );
  };

  // Helper to get confidence class
  const getConfidenceClass = (match: OCRMatch): string => {
    if (match.isExactMatch || match.confidence >= 0.9) return 'high';
    if (match.confidence >= 0.5) return 'medium';
    return 'low';
  };

  // Helper to get confidence label
  const getConfidenceLabel = (match: OCRMatch): string => {
    if (match.isExactMatch || match.confidence >= 0.9) return 'Exact match';
    if (match.confidence >= 0.7) return 'High confidence';
    if (match.confidence >= 0.5) return 'Possible match';
    return 'Low confidence';
  };

  const isProcessing =
    uiState.processingState === 'capturing' ||
    uiState.processingState === 'processing';

  return (
    <div className="ocr-capture">
      <div className="ocr-header">
        <h3>Read Text</h3>
        <button className="close-btn" onClick={onClose}>
          &times;
        </button>
      </div>

      {permissionState === 'prompt' && (
        <div className="permission-prompt">
          <p>Camera access is needed to read box labels.</p>
          <p className="hint">Tap <strong>Allow</strong> when prompted by your browser.</p>
        </div>
      )}
      {permissionState === 'denied' && (
        <div className="permission-denied">
          <p>Camera access is blocked.</p>
          <p className="hint">Enable camera in your browser or device settings, then try again.</p>
        </div>
      )}
      <div className="ocr-video-container">
        <video ref={videoRef} playsInline muted />
        <div className="capture-guide">
          <div className="guide-frame" />
        </div>
        {!hasCamera && permissionState !== 'denied' && permissionState !== 'prompt' && (
          <div className="no-camera">
            <p>Camera unavailable</p>
          </div>
        )}
      </div>

      <div className="ocr-feedback">{renderContent()}</div>

      <div className="ocr-actions">
        {uiState.processingState === 'idle' && (
          <button
            className="capture-btn"
            onClick={handleCapture}
            disabled={!hasCamera || isProcessing}
          >
            Capture &amp; Read
          </button>
        )}
        <button className="cancel-btn" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="ocr-info">
        <small>
          {expectedBarcodes.length} boxes remaining to scan
        </small>
      </div>
    </div>
  );
}

export default OCRCapture;
